/* A product's Baselines tab (designs/product-baselines.md BL20, BL25): the
   product's baselines, New baseline, and one baseline's record.

   A baseline's record shows its state and approval stage, who holds each
   role, the approval panel — Submit, Approve or Reject offered only to the
   person who may act (the server decides, BL11) — the rows in a table built
   from the template's columns, the editor that saves the next version, and
   the log of every version and approval action. */
import { useEffect, useState, type JSX } from "react";
import type { Stamp } from "../../../../shared/organizer-roles";
import type { Product } from "../../../../shared/products";
import {
  APPROVAL_STATE_LABEL,
  STAGE_RULE_LABEL,
  actionsFor,
  latestTemplate,
  moveContent,
  templateAt,
  type BaselineContent,
  type BaselineDetail,
  type BaselineView,
  type Status,
  type TemplateRevision,
  type VersionView,
} from "../../../../shared/baselines";
import { isMine, type Me as IntakeMe } from "../../intake/api";
import { PersonPicker } from "../../intake/PersonPicker";
import { useAppState } from "../../shell/store";
import { getBaseline, getBaselines, getTemplates, message, postBaseline, postEvent, postVersion } from "./api";
import { Person, RowsEditor, RowsTable } from "./Rows";

const day = (s: Stamp): string => s.date.slice(0, 10);

function StateTag({ status }: { status: Pick<Status, "state" | "superseded"> & { stageName?: string | null } }): JSX.Element {
  const cls = status.state === "agreed" ? "evidence" : status.state === "rejected" ? "missing" : status.state === "in-approval" ? "phase-chip" : "unconfirmed";
  const label =
    status.state === "in-approval" && status.stageName ? `In approval: ${status.stageName}` : APPROVAL_STATE_LABEL[status.state];
  return (
    <span className={`state ${status.superseded && status.state !== "agreed" ? "superseded" : cls} baseline-state`}>
      {label}
      {status.superseded ? " · superseded" : ""}
    </span>
  );
}

/* ---- approval (BL10–BL13) ---- */

function Approval({
  detail,
  template,
  me,
  onChanged,
}: {
  detail: BaselineDetail;
  template: TemplateRevision;
  /* /api/me as served — its user carries every id that is this person */
  me: IntakeMe;
  onChanged: (d: BaselineDetail) => void;
}): JSX.Element {
  const current = detail.versions.at(-1)!;
  const { content, status } = current;
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /* every id that is the caller, as the server's samePerson matches
     (Copilot review of #161) */
  const isMe = (userId: string): boolean => isMine(me, userId);
  const can = actionsFor(template, current, isMe);
  const roleLabel = (key: string): string => template.roles.find((r) => r.key === key)?.label ?? key;

  const act = (action: "submit" | "approve" | "reject", roleKey: string): void => {
    setBusy(true);
    setError(null);
    postEvent(detail.baseline.id, current.version, { action, roleKey, comment: comment.trim() === "" ? null : comment, basedOn: current.events.length })
      .then((d) => {
        setComment("");
        onChanged(d);
      })
      .catch((e: unknown) => setError(message(e)))
      .finally(() => setBusy(false));
  };

  /* who approved which stage, from the events */
  const approvedIn = (stageKey: string, roleKey: string) =>
    current.events.find((e) => e.action === "approve" && e.stageKey === stageKey && e.roleKey === roleKey);

  return (
    <section className="card baseline-approval">
      <h3>Approval</h3>
      <p>
        <StateTag status={{ ...status, stageName: status.stage?.name ?? null }} />{" "}
        <span className="faint">
          Owner: {roleLabel(template.ownerRole)} · <Person person={content.roleHolders[template.ownerRole]} />
        </span>
      </p>
      <ol className="baseline-stages">
        {template.stages.map((s) => {
          const here = status.stage?.key === s.key;
          return (
            <li key={s.key} data-stage={s.key} className={here ? "current" : ""}>
              <b>{s.name}</b> <span className="faint">({STAGE_RULE_LABEL[s.rule]})</span>
              <ul>
                {s.roleKeys.map((k) => {
                  const done = approvedIn(s.key, k);
                  return (
                    <li key={k} data-role={k}>
                      {roleLabel(k)}: <Person person={content.roleHolders[k]} />{" "}
                      {done ? (
                        <span className="stamp">
                          approved {day(done.recorded)}
                          {done.comment ? ` — “${done.comment}”` : ""}
                        </span>
                      ) : here ? (
                        <span className="not-set">waiting</span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ol>
      {status.state === "rejected" && (
        <p className="capture-error">
          Rejected by {current.events.at(-1)?.recorded.by?.name ?? "not recorded"}: {current.events.at(-1)?.comment}. The fix is a new version.
        </p>
      )}
      {(can.submit || can.approveAs.length > 0) && (
        <div className="baseline-actions">
          <label className="field">
            <span className="field-label">{can.submit ? "Note (optional)" : "Comment (a reason is required to reject)"}</span>
            <input id="baseline-comment" value={comment} onChange={(e) => setComment(e.target.value)} />
          </label>
          <div className="form-actions">
            {can.submit && (
              <button className="btn" disabled={busy} onClick={() => act("submit", template.ownerRole)}>
                Submit for approval
              </button>
            )}
            {can.approveAs.map((k) => (
              <span key={k} className="baseline-action-pair">
                <button className="btn" disabled={busy} onClick={() => act("approve", k)}>
                  Approve as {roleLabel(k)}
                </button>
                <button className="btn ghost" disabled={busy} onClick={() => act("reject", k)}>
                  Reject as {roleLabel(k)}
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
      {error !== null && (
        <p className="capture-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/* ---- the editor: the next version (BL8) ---- */

function RoleHolders({
  template,
  holders,
  onChange,
}: {
  template: TemplateRevision;
  holders: BaselineContent["roleHolders"];
  onChange: (h: BaselineContent["roleHolders"]) => void;
}): JSX.Element {
  return (
    <div className="baseline-holders">
      {template.roles.map((r) => (
        <PersonPicker
          key={r.key}
          label={r.label}
          value={holders[r.key] ?? null}
          missing="Not recorded — nobody can act in this role until someone is named"
          onChange={(person) => onChange({ ...holders, [r.key]: person })}
        />
      ))}
    </div>
  );
}

function Editor({
  detail,
  onCancel,
  onSaved,
}: {
  detail: BaselineDetail;
  onCancel: () => void;
  onSaved: (d: BaselineDetail) => void;
}): JSX.Element {
  const current = detail.versions.at(-1)!;
  const own = templateAt(detail.templates, detail.baseline.templateId, current.content.templateRevision)!;
  const latest = latestTemplate(detail.templates, detail.baseline.templateId)!;
  const [move, setMove] = useState(false);
  const [content, setContent] = useState<BaselineContent>(current.content);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const template = move ? latest : own;
  const draft = move ? moveContent(content, latest) : content;

  const save = (): void => {
    setBusy(true);
    setError(null);
    postVersion(detail.baseline.id, current.version, draft, note)
      .then(onSaved)
      .catch((e: unknown) => setError(message(e)))
      .finally(() => setBusy(false));
  };

  return (
    <section className="card baseline-editor">
      <h3>Edit — saving makes version {current.version + 1}</h3>
      {current.status.state === "in-approval" && (
        <p className="capture-error">Version {current.version} is in approval. Saving withdraws it; the new version starts as a draft.</p>
      )}
      {current.status.state === "agreed" && <p className="faint">Version {current.version} stays agreed in the log. The new version needs approval again.</p>}
      {latest.revision > own.revision && (
        <label className="baseline-move">
          <input type="checkbox" checked={move} onChange={(e) => setMove(e.target.checked)} /> Move to the template&apos;s latest revision ({latest.revision}). Columns and roles it no longer has are dropped.
        </label>
      )}
      <h4>Who holds each role</h4>
      <RoleHolders template={template} holders={draft.roleHolders} onChange={(roleHolders) => setContent({ ...content, roleHolders: { ...content.roleHolders, ...roleHolders } })} />
      <label className="field">
        <span className="field-label">Summary</span>
        <textarea id="baseline-summary" value={content.summary} onChange={(e) => setContent({ ...content, summary: e.target.value })} />
      </label>
      <label className="field">
        <span className="field-label">Link to the source document</span>
        <input id="baseline-link" value={content.link ?? ""} onChange={(e) => setContent({ ...content, link: e.target.value.trim() === "" ? null : e.target.value })} />
      </label>
      <h4>Rows</h4>
      <RowsEditor template={template} rows={draft.rows} onChange={(rows) => setContent({ ...content, rows })} />
      <label className="field">
        <span className="field-label">What changed</span>
        <input id="baseline-change-note" value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {error !== null && (
        <p className="capture-error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button className="btn ghost" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn" disabled={busy} onClick={save}>
          Save as version {current.version + 1}
        </button>
      </div>
    </section>
  );
}

/* ---- the log (BL12) ---- */

const ACTION_WORD = { submit: "Submitted", approve: "Approved", reject: "Rejected" } as const;

function Log({
  detail,
  viewing,
  onView,
}: {
  detail: BaselineDetail;
  viewing: number;
  onView: (version: number) => void;
}): JSX.Element {
  /* each version's labels come from the template revision it was drafted
     on, so a later rename never rewrites the history (Copilot review of #161) */
  const roleLabel = (v: VersionView, key: string): string =>
    templateAt(detail.templates, detail.baseline.templateId, v.content.templateRevision)?.roles.find((r) => r.key === key)?.label ?? key;
  return (
    <section className="card baseline-log">
      <h3>Versions and approvals</h3>
      <ol className="issue-history" reversed>
        {[...detail.versions].reverse().map((v: VersionView) => (
          <li key={v.version} data-version={v.version}>
            <b>Version {v.version}</b> <StateTag status={{ ...v.status, stageName: v.status.stage?.name ?? null }} /> — {v.changeNote}{" "}
            <span className="faint">
              ({v.recorded.by?.name ?? v.recorded.source}, {day(v.recorded)}; template revision {v.content.templateRevision})
            </span>{" "}
            {v.version === viewing ? (
              <span className="stamp">shown above</span>
            ) : (
              <button className="btn link" onClick={() => onView(v.version)}>
                View version {v.version}
              </button>
            )}
            {v.events.length > 0 && (
              <ul>
                {v.events.map((e) => (
                  <li key={e.seq} className="faint">
                    {ACTION_WORD[e.action]} as {roleLabel(v, e.roleKey)} by {e.recorded.by?.name ?? "not recorded"}, {day(e.recorded)}
                    {e.comment ? ` — “${e.comment}”` : ""}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ---- one baseline ---- */

function BaselineRecord({
  id,
  productName,
  onBack,
  onChanged,
}: {
  id: string;
  /* the R of every row's RACI (BL4) */
  productName: string;
  onBack: () => void;
  onChanged: () => void;
}): JSX.Element {
  const { state } = useAppState();
  const [detail, setDetail] = useState<BaselineDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  /* which version's content is shown; null = the current one */
  const [viewing, setViewing] = useState<number | null>(null);

  useEffect(() => {
    getBaseline(id)
      .then(setDetail)
      .catch((e: unknown) => setError(message(e)));
  }, [id]);

  const changed = (d: BaselineDetail): void => {
    setDetail(d);
    setEditing(false);
    setViewing(null);
    onChanged();
  };

  const back = (
    <div className="form-actions">
      <button className="btn ghost small" onClick={onBack}>
        ← All baselines for {productName}
      </button>
    </div>
  );
  if (error !== null)
    return (
      <div className="baseline-record">
        {back}
        <p className="capture-error" role="alert">
          The baseline could not be read: {error}
        </p>
      </div>
    );
  if (detail === null) return <p className="faint">Loading…</p>;

  const current = detail.versions.at(-1)!;
  const template = templateAt(detail.templates, detail.baseline.templateId, current.content.templateRevision);
  /* BL22: the version in force stays in force while a change is proposed */
  const inForce = detail.inForce === null ? null : detail.versions[detail.inForce - 1]!;
  const proposing = inForce !== null && inForce.version !== current.version;
  /* any version can be read (the use case's "every earlier version"); the
     summary, link and rows all come from the one shown, never mixed with
     the current one (Copilot review of #161) */
  const shown = (viewing !== null ? detail.versions[viewing - 1] : undefined) ?? current;
  const shownTemplate = templateAt(detail.templates, detail.baseline.templateId, shown.content.templateRevision);
  return (
    <div className="baseline-record">
      {back}
      <section className="card">
        <h2>
          {detail.baseline.title} <StateTag status={{ ...current.status, stageName: current.status.stage?.name ?? null }} />
        </h2>
        <p className="faint">
          {template?.name ?? detail.baseline.templateId} · version {current.version}, {current.changeNote} (
          {current.recorded.by?.name ?? current.recorded.source}, {day(current.recorded)})
        </p>
        {inForce === null ? (
          <p className="baseline-in-force" data-in-force="none">
            <span className="state missing">Nothing in force yet</span> — no version has been agreed.
          </p>
        ) : proposing ? (
          <p className="baseline-in-force" data-in-force={inForce.version}>
            <b>In force: version {inForce.version}</b> (agreed). <b>Proposed change: version {current.version}</b>,{" "}
            {APPROVAL_STATE_LABEL[current.status.state].toLowerCase()}
            {current.status.stage ? ` (${current.status.stage.name})` : ""}. Version {inForce.version} stays in force until a change is agreed.
          </p>
        ) : (
          <p className="baseline-in-force" data-in-force={inForce.version}>
            <b>In force: version {inForce.version}</b>.
          </p>
        )}
        {shown !== current && (
          <p className="baseline-viewing" data-viewing={shown.version}>
            Showing version {shown.version}
            {shown.version === inForce?.version ? " (in force)" : ` (${APPROVAL_STATE_LABEL[shown.status.state].toLowerCase()}, not in force)`}.{" "}
            <button className="btn link" onClick={() => setViewing(null)}>
              Show version {current.version}
            </button>
          </p>
        )}
        {shown.content.summary !== "" && <p>{shown.content.summary}</p>}
        {shown.content.link !== null && (
          <p>
            <a href={shown.content.link} target="_blank" rel="noopener noreferrer">
              Source document
            </a>
          </p>
        )}
        {!editing && template && (
          <div className="form-actions">
            <button className="btn ghost small" onClick={() => setEditing(true)}>
              Edit
            </button>
          </div>
        )}
      </section>
      {template === null ? (
        <p className="capture-error">This version&apos;s template revision is missing, so it cannot be shown or approved.</p>
      ) : (
        <>
          {editing ? (
            <Editor detail={detail} onCancel={() => setEditing(false)} onSaved={changed} />
          ) : (
            <>
              <Approval detail={detail} template={template} me={state.me as unknown as IntakeMe} onChanged={changed} />
              {proposing && (
                <div className="about-tabs" role="tablist" aria-label="Which version">
                  <button className={`about-tab${shown === current ? " selected" : ""}`} role="tab" aria-selected={shown === current} onClick={() => setViewing(null)}>
                    Proposed: version {current.version}
                  </button>
                  <button
                    className={`about-tab${shown === inForce ? " selected" : ""}`}
                    role="tab"
                    aria-selected={shown === inForce}
                    onClick={() => setViewing(inForce.version)}
                  >
                    In force: version {inForce.version}
                  </button>
                </div>
              )}
              {shownTemplate && <RowsTable key={shown.version} template={shownTemplate} content={shown.content} productName={productName} />}
            </>
          )}
          <Log detail={detail} viewing={shown.version} onView={setViewing} />
        </>
      )}
    </div>
  );
}

/* ---- a new baseline ---- */

function NewBaselineForm({
  productId,
  templates,
  onCancel,
  onCreated,
}: {
  productId: string;
  templates: TemplateRevision[];
  onCancel: () => void;
  onCreated: (d: BaselineDetail) => void;
}): JSX.Element {
  const [templateId, setTemplateId] = useState(templates[0]?.templateId ?? "");
  const [title, setTitle] = useState("");
  const [holders, setHolders] = useState<BaselineContent["roleHolders"]>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const template = templates.find((t) => t.templateId === templateId) ?? null;

  const create = (): void => {
    if (!template) return;
    setBusy(true);
    setError(null);
    postBaseline({
      productId,
      templateId,
      title,
      changeNote: "Drafted",
      content: {
        templateRevision: template.revision,
        roleHolders: Object.fromEntries(template.roles.map((r) => [r.key, holders[r.key] ?? null])),
        summary: "",
        link: null,
        rows: [],
      },
    })
      .then(onCreated)
      .catch((e: unknown) => setError(message(e)))
      .finally(() => setBusy(false));
  };

  return (
    <section className="card baseline-new">
      <h3>New baseline</h3>
      <label className="field">
        <span className="field-label">Template</span>
        <select id="baseline-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          {templates.map((t) => (
            <option key={t.templateId} value={t.templateId}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      {template && template.description !== "" && <p className="faint">{template.description}</p>}
      <label className="field">
        <span className="field-label">Title</span>
        <input id="baseline-title" value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>
      {template && (
        <>
          <h4>Who holds each role</h4>
          <RoleHolders template={template} holders={holders} onChange={setHolders} />
        </>
      )}
      {error !== null && (
        <p className="capture-error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button className="btn ghost" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn" disabled={busy || template === null} onClick={create}>
          Create draft
        </button>
      </div>
    </section>
  );
}

/* ---- the tab ---- */

type Mode = { kind: "list" } | { kind: "new" } | { kind: "record"; id: string };

export function BaselinesArea({ product }: { product: Product }): JSX.Element {
  const [list, setList] = useState<BaselineView[] | null>(null);
  const [templates, setTemplates] = useState<TemplateRevision[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: "list" });

  const load = (): void => {
    getBaselines(product.id)
      .then(setList)
      .catch((e: unknown) => setError(message(e)));
    getTemplates()
      .then(setTemplates)
      .catch((e: unknown) => setError(message(e)));
  };
  useEffect(load, [product.id]);

  return (
    <section className="baselines-area" data-product={product.id}>
      <div className="banner">
        <b>Baselines.</b>
        <span>
          What {product.name}&apos;s team has agreed to and works against — KPIs, schedule, process — which version is in force, and who approved
          it. A change is a new version that goes through approval again.
        </span>
      </div>
      {error !== null && (
        <p className="capture-error" role="alert">
          The baselines could not be read: {error}
        </p>
      )}
      {mode.kind === "record" ? (
        <BaselineRecord id={mode.id} productName={product.name} onBack={() => setMode({ kind: "list" })} onChanged={load} />
      ) : mode.kind === "new" ? (
        <NewBaselineForm
          productId={product.id}
          templates={templates}
          onCancel={() => setMode({ kind: "list" })}
          onCreated={(d) => {
            load();
            setMode({ kind: "record", id: d.baseline.id });
          }}
        />
      ) : (
        <>
          <div className="issues-bar">
            <span className="spacer" />
            <button className="btn small" disabled={templates.length === 0} onClick={() => setMode({ kind: "new" })}>
              New baseline
            </button>
          </div>
          {list !== null &&
            (list.length === 0 ? (
              <p className="empty">No baselines are recorded for {product.name} yet.</p>
            ) : (
              <div className="dash-table">
                <table className="recs baselines-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Template</th>
                      <th>In force</th>
                      <th>Latest version</th>
                      <th>State</th>
                      <th>Changed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((a) => (
                      <tr key={a.id} className="rec">
                        <td>
                          <button className="btn link baseline-open" onClick={() => setMode({ kind: "record", id: a.id })}>
                            {a.title}
                          </button>
                        </td>
                        <td>{a.templateName}</td>
                        <td className="num">{a.inForce === null ? <span className="not-set">None yet</span> : `v${a.inForce}`}</td>
                        <td className="num">{a.version}</td>
                        <td>
                          <StateTag status={{ state: a.state, superseded: false, stageName: a.stageName }} />
                        </td>
                        <td className="num">{day(a.updated)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </>
      )}
    </section>
  );
}
